import React, { useEffect } from 'react';
import { Avatar, Box, Typography, Stack } from 'omaha-design-system';
import type { NbaRatesPlayerStats, NbaRatesAggregateStats } from './types';
import { DEFAULT_AVATAR } from './types';
import {
  formatPct,
  formatDec1,
  formatDec2,
  calcDkFpts,
  calcFptsPerMin,
  calcPer100,
  calcRelativeDelta,
  getDeltaBackground,
  RELATIVE_THRESHOLD,
} from './statsCalculations';

// ─── Types ───────────────────────────────────────────────────────────────────

interface NbaRatesPlayerDetailCardProps {
  player: NbaRatesPlayerStats;
  baselinePlayer: NbaRatesPlayerStats;
  filteredAggregate: NbaRatesAggregateStats;
  baselineAggregate: NbaRatesAggregateStats;
  teamName: string;
  filterTerms: string[];
  onClose: () => void;
}

interface StatRowDef {
  label: string;
  sample: (p: NbaRatesPlayerStats) => number | null;
  season: (p: NbaRatesPlayerStats) => number | null;
  format: (v: number | null) => string;
  suffix?: string;
  /** Only rate/percentage stats get delta coloring (totals are always smaller than season) */
  isRate?: boolean;
}

// ─── Per-100 Helpers ─────────────────────────────────────────────────────────

const offPer100 = (p: NbaRatesPlayerStats, val: number): number | null =>
  calcPer100(val, p.off_poss || p.possessions || 1);

const defPer100 = (p: NbaRatesPlayerStats, val: number): number | null =>
  calcPer100(val, p.def_poss || p.possessions || 1);

// ─── Stat Sections ───────────────────────────────────────────────────────────

const fmtPer100MadeAttempts = (m: number | null, a: number | null) =>
  `${formatDec1(m)}/${formatDec1(a)}`;

const LEFT_SECTIONS: { title: string; rows: StatRowDef[] }[] = [
  {
    title: 'Volume',
    rows: [
      { label: 'MIN', sample: (p) => p.minutes, season: (p) => p.minutes, format: (v) => formatDec1(v) },
      { label: 'POSS', sample: (p) => p.off_poss, season: (p) => p.off_poss, format: (v) => formatDec1(v) },
    ],
  },
  {
    title: 'Fantasy',
    rows: [
      { label: 'FPTS (DK)', sample: (p) => offPer100(p, calcDkFpts(p)), season: (p) => offPer100(p, calcDkFpts(p)), format: (v) => formatDec1(v), isRate: true },
      { label: 'FP/M', sample: (p) => calcFptsPerMin(p), season: (p) => calcFptsPerMin(p), format: (v) => formatDec2(v), isRate: true },
    ],
  },
  {
    title: 'Scoring',
    rows: [
      { label: 'PTS', sample: (p) => offPer100(p, p.pts), season: (p) => offPer100(p, p.pts), format: (v) => formatDec1(v), isRate: true },
      { label: '2PM/A', sample: (p) => null, season: (p) => null, format: () => '' },
      { label: '3PM/A', sample: (p) => null, season: (p) => null, format: () => '' },
      { label: 'FTM/A', sample: (p) => null, season: (p) => null, format: () => '' },
      { label: 'TS%', sample: (p) => p.ts_pct, season: (p) => p.ts_pct, format: (v) => formatPct(v), suffix: '%', isRate: true },
      { label: 'eFG%', sample: (p) => p.efg_pct, season: (p) => p.efg_pct, format: (v) => formatPct(v), suffix: '%', isRate: true },
      { label: 'FG2%', sample: (p) => p.fg2_pct, season: (p) => p.fg2_pct, format: (v) => formatPct(v), suffix: '%', isRate: true },
      { label: 'FG3%', sample: (p) => p.fg3_pct, season: (p) => p.fg3_pct, format: (v) => formatPct(v), suffix: '%', isRate: true },
      { label: 'FT%', sample: (p) => p.ft_pct, season: (p) => p.ft_pct, format: (v) => formatPct(v), suffix: '%', isRate: true },
      { label: 'USG%', sample: (p) => p.usage, season: (p) => p.usage, format: (v) => formatPct(v), suffix: '%', isRate: true },
    ],
  },
];

const RIGHT_SECTIONS: { title: string; rows: StatRowDef[] }[] = [
  {
    title: 'Playmaking',
    rows: [
      { label: 'AST', sample: (p) => offPer100(p, p.ast), season: (p) => offPer100(p, p.ast), format: (v) => formatDec1(v), isRate: true },
      { label: 'AST Ratio', sample: (p) => p.ast_ratio, season: (p) => p.ast_ratio, format: (v) => formatPct(v), suffix: '%', isRate: true },
      { label: 'TOV', sample: (p) => offPer100(p, p.tov), season: (p) => offPer100(p, p.tov), format: (v) => formatDec1(v), isRate: true },
      { label: 'TOV%', sample: (p) => p.tov_pct, season: (p) => p.tov_pct, format: (v) => formatPct(v), suffix: '%', isRate: true },
    ],
  },
  {
    title: 'Rebounding',
    rows: [
      { label: 'OREB', sample: (p) => offPer100(p, p.oreb), season: (p) => offPer100(p, p.oreb), format: (v) => formatDec1(v), isRate: true },
      { label: 'DREB', sample: (p) => defPer100(p, p.dreb), season: (p) => defPer100(p, p.dreb), format: (v) => formatDec1(v), isRate: true },
      { label: 'ORB%', sample: (p) => p.off_reb_pct, season: (p) => p.off_reb_pct, format: (v) => formatPct(v), suffix: '%', isRate: true },
      { label: 'DRB%', sample: (p) => p.def_reb_pct, season: (p) => p.def_reb_pct, format: (v) => formatPct(v), suffix: '%', isRate: true },
    ],
  },
  {
    title: 'Defense',
    rows: [
      { label: 'STL', sample: (p) => defPer100(p, p.stl), season: (p) => defPer100(p, p.stl), format: (v) => formatDec1(v), isRate: true },
      { label: 'BLK', sample: (p) => defPer100(p, p.blk), season: (p) => defPer100(p, p.blk), format: (v) => formatDec1(v), isRate: true },
    ],
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

const NbaRatesPlayerDetailCard: React.FC<NbaRatesPlayerDetailCardProps> = ({
  player,
  baselinePlayer,
  filteredAggregate,
  baselineAggregate,
  teamName,
  filterTerms,
  onClose,
}) => {
  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  /** Render per-100 made/attempts cells for shooting rows */
  const renderMadeAttempts = (
    samplePlayer: NbaRatesPlayerStats,
    seasonPlayer: NbaRatesPlayerStats,
    madeKey: 'fg2m' | 'fg3m' | 'ftm',
    attKey: 'fg2a' | 'fg3a' | 'fta',
  ) => {
    const sampleStr = fmtPer100MadeAttempts(
      offPer100(samplePlayer, samplePlayer[madeKey]),
      offPer100(samplePlayer, samplePlayer[attKey]),
    );
    const seasonStr = fmtPer100MadeAttempts(
      offPer100(seasonPlayer, seasonPlayer[madeKey]),
      offPer100(seasonPlayer, seasonPlayer[attKey]),
    );
    return (
      <>
        <Box component="td" sx={valueCellSx}>{sampleStr}</Box>
        <Box component="td" sx={valueCellSx}>{seasonStr}</Box>
      </>
    );
  };

  const renderSections = (sections: { title: string; rows: StatRowDef[] }[]) =>
    sections.map((section) => (
      <React.Fragment key={section.title}>
        <Box component="tr">
          <Box
            component="td"
            colSpan={3}
            sx={{
              pt: 1.5,
              pb: 0.5,
              fontSize: '0.65rem',
              fontWeight: 700,
              color: '#3B82F6',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              borderBottom: '1px solid #1E2330',
            }}
          >
            {section.title}
          </Box>
        </Box>

        {section.rows.map((row) => {
          if (row.label === '2PM/A') {
            return (
              <Box component="tr" key={row.label} sx={rowSx}>
                <Box component="td" sx={labelCellSx}>2PM/A</Box>
                {renderMadeAttempts(player, baselinePlayer, 'fg2m', 'fg2a')}
              </Box>
            );
          }
          if (row.label === '3PM/A') {
            return (
              <Box component="tr" key={row.label} sx={rowSx}>
                <Box component="td" sx={labelCellSx}>3PM/A</Box>
                {renderMadeAttempts(player, baselinePlayer, 'fg3m', 'fg3a')}
              </Box>
            );
          }
          if (row.label === 'FTM/A') {
            return (
              <Box component="tr" key={row.label} sx={rowSx}>
                <Box component="td" sx={labelCellSx}>FTM/A</Box>
                {renderMadeAttempts(player, baselinePlayer, 'ftm', 'fta')}
              </Box>
            );
          }

          const sampleVal = row.sample(player);
          const seasonVal = row.season(baselinePlayer);
          const delta = row.isRate ? calcRelativeDelta(sampleVal, seasonVal) : null;
          const bg = getDeltaBackground(delta);

          let sampleColor = '#E8EAED';
          if (delta !== null && Math.abs(delta) >= RELATIVE_THRESHOLD) {
            sampleColor = delta > 0 ? '#10B981' : '#EF4444';
          }

          const sampleText = row.format(sampleVal) + (row.suffix && sampleVal != null ? row.suffix : '');
          const seasonText = row.format(seasonVal) + (row.suffix && seasonVal != null ? row.suffix : '');

          return (
            <Box component="tr" key={row.label} sx={rowSx}>
              <Box component="td" sx={labelCellSx}>{row.label}</Box>
              <Box
                component="td"
                sx={{
                  ...valueCellSx,
                  color: sampleColor,
                  bgcolor: bg,
                  fontWeight: delta !== null && Math.abs(delta) >= RELATIVE_THRESHOLD ? 600 : 500,
                }}
              >
                {sampleText}
              </Box>
              <Box component="td" sx={valueCellSx}>
                {seasonText}
              </Box>
            </Box>
          );
        })}
      </React.Fragment>
    ));

  const renderRatingsSection = () => (
    <React.Fragment>
      <Box component="tr">
        <Box
          component="td"
          colSpan={3}
          sx={{
            pt: 1.5,
            pb: 0.5,
            fontSize: '0.65rem',
            fontWeight: 700,
            color: '#3B82F6',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            borderBottom: '1px solid #1E2330',
          }}
        >
          Ratings
        </Box>
      </Box>
      {([
        { label: 'ORtg', sampleVal: filteredAggregate.off_rtg, seasonVal: baselineAggregate.off_rtg },
        { label: 'DRtg', sampleVal: filteredAggregate.def_rtg, seasonVal: baselineAggregate.def_rtg },
        { label: 'Net Rtg', sampleVal: filteredAggregate.net_rtg, seasonVal: baselineAggregate.net_rtg },
      ] as const).map(({ label, sampleVal, seasonVal }) => {
        const delta = calcRelativeDelta(sampleVal, seasonVal);
        const bg = getDeltaBackground(delta);
        let sampleColor = '#E8EAED';
        if (delta !== null && Math.abs(delta) >= RELATIVE_THRESHOLD) {
          sampleColor = delta > 0 ? '#10B981' : '#EF4444';
        }
        const fmtRtg = (v: number | null) => {
          if (v == null) return '-';
          if (label === 'Net Rtg') return (v > 0 ? '+' : '') + v.toFixed(1);
          return v.toFixed(1);
        };
        const adjustedColor = label === 'DRtg' && delta !== null && Math.abs(delta) >= RELATIVE_THRESHOLD
          ? (delta > 0 ? '#EF4444' : '#10B981')
          : sampleColor;
        return (
          <Box component="tr" key={label} sx={rowSx}>
            <Box component="td" sx={labelCellSx}>{label}</Box>
            <Box
              component="td"
              sx={{
                ...valueCellSx,
                color: adjustedColor,
                bgcolor: bg,
                fontWeight: delta !== null && Math.abs(delta) >= RELATIVE_THRESHOLD ? 600 : 500,
              }}
            >
              {fmtRtg(sampleVal)}
            </Box>
            <Box component="td" sx={valueCellSx}>{fmtRtg(seasonVal)}</Box>
          </Box>
        );
      })}
    </React.Fragment>
  );

  const renderTable = (sections: { title: string; rows: StatRowDef[] }[], includeRatings?: boolean) => (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <Box component="thead">
        <Box component="tr">
          <Box component="th" sx={headerLabelSx}>Stat</Box>
          <Box component="th" sx={headerValueSx}>Sample</Box>
          <Box component="th" sx={headerValueSx}>Season</Box>
        </Box>
      </Box>
      <Box component="tbody">
        {renderSections(sections)}
        {includeRatings && renderRatingsSection()}
      </Box>
    </Box>
  );

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1300,
          backdropFilter: 'blur(4px)',
        }}
      />

      {/* Card */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1301,
          width: 760,
          maxHeight: '85vh',
          overflowY: 'auto',
          bgcolor: '#0D0F13',
          border: '1px solid #1E2330',
          borderRadius: '12px',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.6)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            background: 'linear-gradient(135deg, #1A1D24 0%, #14171C 100%)',
            borderBottom: '1px solid #1E2330',
            position: 'relative',
          }}
        >
          <Avatar
            src={DEFAULT_AVATAR}
            alt={player.player_name}
            sx={{ width: 48, height: 48, flexShrink: 0 }}
          />
          <Stack spacing={0.25} sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#E8EAED' }}>
              {player.player_name}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6B7280' }}>
              {teamName}
            </Typography>
          </Stack>

          {/* Filter terms */}
          {filterTerms.length > 0 && (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mr: 4 }}>
              {filterTerms.map((term, i) => (
                <Box
                  key={i}
                  sx={{
                    px: 1,
                    py: 0.25,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: '#C9CDD4',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid #1E2330',
                    borderRadius: '6px',
                  }}
                >
                  {term}
                </Box>
              ))}
            </Stack>
          )}

          <Box
            component="button"
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              background: 'none',
              border: 'none',
              color: '#6B7280',
              cursor: 'pointer',
              fontSize: '1.1rem',
              lineHeight: 1,
              p: 0.5,
              borderRadius: '4px',
              '&:hover': { color: '#E8EAED', bgcolor: '#1E2330' },
            }}
          >
            {'\u2715'}
          </Box>
        </Box>

        {/* Two-column stat tables */}
        <Box sx={{ display: 'flex', gap: 0, p: 2 }}>
          <Box sx={{ flex: 1, pr: 1.5 }}>
            {renderTable(LEFT_SECTIONS)}
          </Box>
          <Box sx={{ width: '1px', bgcolor: '#1E2330', my: -2 }} />
          <Box sx={{ flex: 1, pl: 1.5 }}>
            {renderTable(RIGHT_SECTIONS, true)}
          </Box>
        </Box>

        <Typography sx={{ px: 2, pb: 1.5, fontSize: '0.6rem', color: '#4B5563' }}>
          Counting stats shown per 100 possessions
        </Typography>
      </Box>
    </>
  );
};

// ─── Shared Styles ───────────────────────────────────────────────────────────

const headerLabelSx = {
  textAlign: 'left' as const,
  fontSize: '0.65rem',
  fontWeight: 700,
  color: '#6B7280',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  pb: 0.75,
  borderBottom: '2px solid #1E2330',
};

const headerValueSx = {
  textAlign: 'center' as const,
  fontSize: '0.65rem',
  fontWeight: 700,
  color: '#6B7280',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  pb: 0.75,
  borderBottom: '2px solid #1E2330',
  width: 90,
};

const rowSx = {
  '&:hover': { bgcolor: '#1A1D24' },
  transition: 'background 0.1s ease',
};

const labelCellSx = {
  py: 0.75,
  pr: 1,
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#9CA3AF',
  borderBottom: '1px solid rgba(30, 35, 48, 0.5)',
};

const valueCellSx = {
  py: 0.75,
  textAlign: 'center' as const,
  fontSize: '0.8rem',
  fontWeight: 500,
  color: '#E8EAED',
  fontVariantNumeric: 'tabular-nums' as const,
  borderBottom: '1px solid rgba(30, 35, 48, 0.5)',
  transition: 'background 0.1s ease',
};

export default NbaRatesPlayerDetailCard;
